import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { supabase } from '../services/supabase';

export default function PackageDrawer({
  isOpen,
  onClose,
  delivery,
  workOrders = [],
  kanbanJobs = [],
  onPreviewPDF,
}) {
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && delivery) {
      fetchDeliveryDetails();
    }
  }, [isOpen, delivery]);

  const fetchDeliveryDetails = async () => {
    if (!delivery) return;
    setLoading(true);

    try {
      // Fetch all delivery items for this package
      const { data: items, error } = await supabase
        .from('deliveries')
        .select(`
          delivery_id,
          quantity,
          kanban_job_id,
          job_id,
          kanban_jobs (
            po_line_number,
            part_number,
            description
          )
        `)
        .eq('package_number', delivery.package_number);

      if (error) throw error;
      setDeliveryItems(items || []);
    } catch (err) {
      console.error('Error fetching delivery details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!delivery) return null;

  const linkedWorkOrder = delivery.job_id
    ? workOrders.find(wo => wo.id === delivery.job_id)
    : null;

  const totalQuantity = deliveryItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Helper to format dates without timezone shift
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    {/* Header */}
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-base font-semibold text-slate-900 font-mono">
                          {delivery.package_number}
                        </Dialog.Title>
                        <button
                          onClick={onClose}
                          className="p-1 rounded-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto">
                      {/* Summary */}
                      <div className="px-4 py-4 border-b border-slate-100">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Ship Date</p>
                            <p className="font-medium text-slate-800">
                              {formatDate(delivery.ship_date)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Total Quantity</p>
                            <p className="font-medium text-slate-800 tabular-nums">
                              {totalQuantity || delivery.quantity}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Invoice</p>
                            {delivery.invoice_number ? (
                              <span className="inline-block px-1.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-sm border border-emerald-200">
                                {delivery.invoice_number}
                              </span>
                            ) : (
                              <span className="inline-block px-1.5 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-sm border border-amber-200">
                                Uninvoiced
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Work Order</p>
                            {linkedWorkOrder ? (
                              <p className="font-mono text-sm text-slate-800">
                                {linkedWorkOrder.po_number}-{linkedWorkOrder.po_line_number}
                              </p>
                            ) : (
                              <p className="text-slate-400 text-sm">None</p>
                            )}
                          </div>
                        </div>

                        {/* Packing Slip */}
                        {delivery.packing_slip_path && (
                          <div className="mt-4">
                            <button
                              onClick={() => onPreviewPDF?.(delivery.packing_slip_path, 'delivery-files')}
                              className="w-full px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              View Packing Slip
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Package Contents */}
                      <div className="px-4 py-3">
                        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">
                          Package Contents
                        </h3>
                        {loading ? (
                          <p className="text-sm text-slate-500">Loading...</p>
                        ) : deliveryItems.length === 0 ? (
                          <p className="text-sm text-slate-500">No items found.</p>
                        ) : (
                          <div className="space-y-2">
                            {deliveryItems.map((item) => {
                              const kanbanJob = item.kanban_jobs || kanbanJobs.find(j => j.id === item.kanban_job_id);
                              return (
                                <div
                                  key={item.delivery_id}
                                  className="p-3 bg-slate-50 rounded-sm border border-slate-100"
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-mono font-medium text-slate-800 text-sm">
                                        {kanbanJob?.part_number || 'Unknown Part'}
                                      </p>
                                      {kanbanJob?.description && (
                                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                          {kanbanJob.description}
                                        </p>
                                      )}
                                      <p className="text-xs text-slate-400 mt-1">
                                        Line {kanbanJob?.po_line_number || '-'}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-lg font-semibold text-slate-800 tabular-nums">
                                        {item.quantity}
                                      </p>
                                      <p className="text-xs text-slate-500">units</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
